// ══════════════════════════════════════════════════════════════════════════════�?// Auto-translation hint popover (shared for user-entered translated content)
// ══════════════════════════════════════════════════════════════════════════════�?
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslationUiEnabled } from '../../contexts/FeaturesContext';
import { LanguageLineIcon } from './LanguageLineIcon';

type AutoTranslationHintProps = {
  targetLanguageLabel: string;
  showingOriginal: boolean;
  onToggleOriginal: () => void;
  className?: string;
};

type PanelPosition = {
  top: number;
  left: number;
};

const PANEL_WIDTH_PX = 224;
const PANEL_ESTIMATED_HEIGHT_PX = 148;
const VIEWPORT_EDGE_PADDING_PX = 8;
const PANEL_GAP_PX = 8;

const resolvePanelPosition = (
  triggerRect: DOMRect,
  panelWidth: number,
  panelHeight: number,
): PanelPosition => {
  const pad = VIEWPORT_EDGE_PADDING_PX;
  const maxLeft = Math.max(pad, window.innerWidth - panelWidth - pad);

  const belowTop = triggerRect.bottom + PANEL_GAP_PX;
  const aboveTop = triggerRect.top - panelHeight - PANEL_GAP_PX;
  const fitsBelow = belowTop + panelHeight <= window.innerHeight - pad;
  const top = fitsBelow ? belowTop : Math.max(pad, aboveTop);

  const endAlignedLeft = triggerRect.right - panelWidth;
  const startAlignedLeft = triggerRect.left;
  const left =
    endAlignedLeft < pad
      ? Math.min(startAlignedLeft, maxLeft)
      : Math.max(pad, Math.min(endAlignedLeft, maxLeft));

  return { top, left };
};

export const AutoTranslationHint = ({
  targetLanguageLabel,
  showingOriginal,
  onToggleOriginal,
  className = '',
}: AutoTranslationHintProps) => {
  const translationUiEnabled = useTranslationUiEnabled();
  const { t } = useLanguage();
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState<PanelPosition>({ top: 0, left: 0 });

  const handleTogglePanel = useCallback((): void => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback((): void => {
    setOpen(false);
  }, []);

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleTogglePanel();
      }
    },
    [handleClose, handleTogglePanel],
  );

  const handleToggleOriginalClick = useCallback((): void => {
    onToggleOriginal();
  }, [onToggleOriginal]);

  const handleToggleOriginalKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleOriginal();
      }
    },
    [onToggleOriginal],
  );

  const updatePanelPosition = useCallback((): void => {
    if (!rootRef.current) {
      return;
    }

    const triggerRect = rootRef.current.getBoundingClientRect();
    const panelWidth = panelRef.current?.offsetWidth ?? PANEL_WIDTH_PX;
    const panelHeight = panelRef.current?.offsetHeight ?? PANEL_ESTIMATED_HEIGHT_PX;
    setPanelPosition(resolvePanelPosition(triggerRect, panelWidth, panelHeight));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    updatePanelPosition();
    const frameId = window.requestAnimationFrame(() => {
      updatePanelPosition();
    });

    const handleReposition = (): void => {
      updatePanelPosition();
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, targetLanguageLabel, updatePanelPosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      handleClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [handleClose, open]);

  if (!translationUiEnabled) {
    return null;
  }

  const panelNode =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={t('translation.hintPanelAria')}
            className="fixed z-[9999] w-56 max-w-[calc(100vw-1rem)] rounded-2xl border border-gray-200/80 bg-white p-4 shadow-xl dark:border-gray-600 dark:bg-gray-900"
            style={{ top: panelPosition.top, left: panelPosition.left }}
          >
            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
              {t('translation.autoTranslatedTo', { language: targetLanguageLabel })}
            </p>
            <button
              type="button"
              className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              aria-pressed={showingOriginal}
              onClick={handleToggleOriginalClick}
              onKeyDown={handleToggleOriginalKeyDown}
            >
              {showingOriginal ? t('translation.showTranslated') : t('translation.showOriginal')}
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={`relative shrink-0 ${className}`}>
      <button
        type="button"
        className="inline-flex shrink-0 items-center justify-center p-0 text-gray-500 transition-colors hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30 dark:text-gray-400 dark:hover:text-emerald-300"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('translation.hintButtonAria')}
        onClick={handleTogglePanel}
        onKeyDown={handleTriggerKeyDown}
      >
        <LanguageLineIcon className="h-5 w-5" />
      </button>
      {panelNode}
    </div>
  );
};
